import { Component, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { catchError, of } from 'rxjs';
import { MarketingCampaignApiService } from '../services/marketing-campaign-api.service';
import {
  CampaignMetrics,
  CampaignRecipient,
  CampaignRecipientStatus,
  EmailMessageStatus,
} from '../models/marketing-campaign.model';
import { EmailMessageApiService } from '../services/email-message-api.service';
import { MyEmailDetail } from '../models/email-channel.model';

interface RowState {
  isExpanded?: boolean;
  detail?: MyEmailDetail;
  detailLoading?: boolean;
}

@Component({
  selector: 'app-campaign-shipping',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './campaign-shipping.component.html',
})
export class CampaignShippingComponent implements OnInit, OnChanges {
  @Input() campaignId!: number;

  /**
   * Métricas agregadas autoritativas de la campaña (mismas que muestra la
   * pestaña "Métricas"). Cuando llegan, el resumen del envío se calcula a
   * partir de ellas — NO de la página de destinatarios cargada — de modo que
   * "Envío" y "Métricas" muestran exactamente los mismos números (Inc 5 / 36).
   */
  @Input() metrics: CampaignMetrics | null = null;

  private readonly api = inject(MarketingCampaignApiService);
  private readonly emailApi = inject(EmailMessageApiService);

  loading = false;
  error: string | null = null;

  rows: CampaignRecipient[] = [];
  rowState = new Map<number, RowState>();

  page = 0;
  pageSize = 50;
  totalElements = 0;
  totalPages = 0;
  hasMore = false;

  searchTerm = '';
  statusFilter: CampaignRecipientStatus | 'ALL' = 'ALL';

  readonly statuses: CampaignRecipientStatus[] = [
    'PENDING', 'QUEUED', 'SENT', 'FAILED', 'SKIPPED',
  ];

  ngOnInit(): void {
    if (this.campaignId) this.load(true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['campaignId'] && this.campaignId) {
      this.rows = [];
      this.rowState.clear();
      this.page = 0;
      this.load(true);
    }
  }

  load(reset: boolean): void {
    if (!this.campaignId) return;
    if (reset) {
      this.rows = [];
      this.rowState.clear();
      this.page = 0;
    }
    this.loading = true;
    this.error = null;

    this.api.listRecipients(this.campaignId, {
      status: this.statusFilter === 'ALL' ? undefined : this.statusFilter,
      page: this.page,
      size: this.pageSize,
    }).pipe(
      catchError(err => {
        this.error = err?.error?.message || 'No se pudieron cargar los destinatarios.';
        return of(null);
      })
    ).subscribe(res => {
      this.loading = false;
      if (!res) return;
      this.rows = reset ? res.content : [...this.rows, ...res.content];
      this.totalElements = res.totalElements;
      this.totalPages = res.totalPages;
      this.hasMore = res.hasNext ?? !res.last;
    });
  }

  loadMore(): void {
    if (!this.hasMore || this.loading) return;
    this.page++;
    this.load(false);
  }

  setStatusFilter(s: CampaignRecipientStatus | 'ALL'): void {
    if (this.statusFilter === s) return;
    this.statusFilter = s;
    this.load(true);
  }

  toggleExpand(row: CampaignRecipient): void {
    const state = this.rowState.get(row.id) || {};
    state.isExpanded = !state.isExpanded;
    this.rowState.set(row.id, state);

    if (state.isExpanded && !state.detail && row.idEmailMessage) {
      state.detailLoading = true;
      this.rowState.set(row.id, state);
      this.emailApi.getDetail(row.idEmailMessage).subscribe({
        next: (detail) => {
          const s = this.rowState.get(row.id) || {};
          s.detail = detail;
          s.detailLoading = false;
          this.rowState.set(row.id, s);
        },
        error: () => {
          const s = this.rowState.get(row.id) || {};
          s.detailLoading = false;
          this.rowState.set(row.id, s);
        },
      });
    }
  }

  getState(rowId: number): RowState {
    return this.rowState.get(rowId) || {};
  }

  // ===== Filtered (búsqueda local — el filtro de status pasa al server) =====
  get filteredRows(): CampaignRecipient[] {
    if (!this.searchTerm) return this.rows;
    const t = this.searchTerm.toLowerCase();
    return this.rows.filter(r =>
      (r.email || '').toLowerCase().includes(t)
      || (r.fullName || '').toLowerCase().includes(t));
  }

  // ===== Resumen autoritativo (Inc 5 / 36) =====
  // Prefiere las métricas agregadas del backend (idénticas a la pestaña
  // "Métricas"); si aún no llegaron, cae al conteo de la página cargada.
  /** true cuando el resumen usa las métricas autoritativas del backend. */
  get hasAuthoritativeMetrics(): boolean {
    return this.metrics !== null;
  }
  get sumTotal(): number {
    return this.metrics?.totalRecipients ?? this.totalElements;
  }
  get sumPending(): number {
    return this.metrics?.pending ?? this.countByStatus('PENDING');
  }
  get sumQueued(): number {
    return this.metrics?.queued ?? this.countByStatus('QUEUED');
  }
  get sumSent(): number {
    return this.metrics?.sent ?? this.countByStatus('SENT');
  }
  get sumFailed(): number {
    return this.metrics?.failed ?? this.countByStatus('FAILED');
  }
  get sumSkipped(): number {
    return this.metrics?.skipped ?? this.countByStatus('SKIPPED');
  }
  get sumDelivered(): number {
    return this.metrics?.delivered ?? this.deliveredCount;
  }
  get sumBounced(): number {
    return this.metrics?.bounced ?? this.bouncedCount;
  }
  get sumOpened(): number {
    return this.metrics?.opens?.unique ?? this.openedCount;
  }
  get sumClicked(): number {
    return this.metrics?.clicks?.unique ?? this.clickedCount;
  }
  /** Tasa de apertura en % (aperturas únicas / entregados). Entero. */
  get sumOpenRate(): number {
    const r = this.metrics?.opens?.openRate;
    return r !== null && r !== undefined ? Math.round(r * 100) : this.openedRate;
  }
  /** Tasa de clics en % (clics únicos / entregados). Entero. */
  get sumClickRate(): number {
    const r = this.metrics?.clicks?.clickRate;
    return r !== null && r !== undefined ? Math.round(r * 100) : this.clickedRate;
  }

  // ===== KPIs locales (de la página visible) =====
  countByStatus(s: CampaignRecipientStatus): number {
    return this.rows.filter(r => r.recipientStatus === s).length;
  }

  get openedCount(): number {
    return this.rows.filter(r => (r.openCount ?? 0) > 0).length;
  }

  get clickedCount(): number {
    return this.rows.filter(r => (r.clickCount ?? 0) > 0).length;
  }

  get bouncedCount(): number {
    return this.rows.filter(r => r.emailStatus === 'BOUNCED').length;
  }

  get deliveredCount(): number {
    return this.rows.filter(r => r.deliveredAt
      || r.emailStatus === 'DELIVERED'
      || r.emailStatus === 'OPENED'
      || r.emailStatus === 'CLICKED').length;
  }

  get openedRate(): number {
    return this.rows.length > 0
      ? Math.round((this.openedCount / this.rows.length) * 100)
      : 0;
  }

  get clickedRate(): number {
    return this.rows.length > 0
      ? Math.round((this.clickedCount / this.rows.length) * 100)
      : 0;
  }

  // ===== Helpers =====
  formatDate(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '—' : d.toLocaleString('es', { dateStyle: 'short', timeStyle: 'short' });
  }

  recipientStatusBadge(status: CampaignRecipientStatus): string {
    const map: Record<CampaignRecipientStatus, string> = {
      PENDING: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
      QUEUED: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      SENT: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
      FAILED: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
      SKIPPED: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    };
    return map[status] || map.PENDING;
  }

  recipientStatusLabel(status: CampaignRecipientStatus): string {
    const map: Record<CampaignRecipientStatus, string> = {
      PENDING: 'Pendiente', QUEUED: 'Encolado', SENT: 'Enviado',
      FAILED: 'Falló', SKIPPED: 'Saltado',
    };
    return map[status] || status;
  }

  /** True si el recipientError corresponde a una exclusión (no un fallo real). */
  isExclusionError(error: string | null | undefined): boolean {
    return error === 'EXCLUDED_BY_CAMPAIGN' || error === 'EXCLUDED_BY_SUPPRESSION';
  }

  /** Etiqueta corta para badge cuando el motivo del SKIPPED es una exclusión. */
  exclusionLabel(error: string | null | undefined): string {
    if (error === 'EXCLUDED_BY_CAMPAIGN') return 'Excluido manualmente';
    if (error === 'EXCLUDED_BY_SUPPRESSION') return 'En lista de supresión';
    return '';
  }

  exclusionTooltip(error: string | null | undefined): string {
    if (error === 'EXCLUDED_BY_CAMPAIGN') {
      return 'Filtrado por la lista de exclusión configurada en este correo.';
    }
    if (error === 'EXCLUDED_BY_SUPPRESSION') {
      return 'Filtrado por una suppression-list global del business.';
    }
    return '';
  }

  emailStatusBadge(status: EmailMessageStatus | null | undefined): string {
    if (!status) return 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400';
    const map: Record<string, string> = {
      QUEUED: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
      SENDING: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      SENT: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      DELIVERED: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
      OPENED: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      CLICKED: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
      BOUNCED: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
      COMPLAINED: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
      FAILED: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    };
    return map[status] || map['QUEUED'];
  }

  emailStatusLabel(status: string | EmailMessageStatus | null | undefined): string {
    if (!status) return '—';
    const map: Record<string, string> = {
      QUEUED: 'Encolado', SENDING: 'Enviando', SENT: 'Enviado',
      DELIVERED: 'Entregado', OPENED: 'Abierto', CLICKED: 'Click',
      BOUNCED: 'Rebotado', COMPLAINED: 'Spam', FAILED: 'Falló',
    };
    return map[status] || status;
  }

  eventIcon(type: string): string {
    const map: Record<string, string> = {
      QUEUED: '🕐', SENT: '📤', DELIVERED: '✅',
      OPENED: '👁', CLICKED: '🖱',
      BOUNCED: '❌', COMPLAINED: '🚫', FAILED: '⚠️',
    };
    return map[type] || '•';
  }

  initialsOf(name: string | undefined, email: string): string {
    const src = (name || email || '').trim();
    if (!src) return '?';
    return src.split(/[\s@]+/).map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  }
}
